import { Injectable, inject as inject_1 } from '@angular/core';

import * as jsts from 'jsts';
import GeoJSON from 'ol/format/GeoJSON';
import {
  LineString,
  LinearRing,
  MultiLineString,
  MultiPoint,
  MultiPolygon,
  Point,
  Polygon,
  Geometry,
} from 'ol/geom';
import { Feature } from 'src/app/geo/models';

import { CombineGeometryPrepareResult } from '../models';
import { JstsHelperService } from './jsts-helper.service';

@Injectable({
  providedIn: 'root',
})
export class CombineGeometryService {
  private readonly jstsHelper = inject_1(JstsHelperService);

  prepare(fieldFeature1: Feature, fieldFeature2: Feature) {
    const result: CombineGeometryPrepareResult = {
      success: false,
      isIntersecting: false,
      hasCorrectNumberOfFieldFeatures: !!(fieldFeature1 && fieldFeature2),
      parser: null,
      format: null,
      field1Geom: null,
      field2Geom: null,
    };

    if (!result.hasCorrectNumberOfFieldFeatures) {
      return result;
    }

    result.format = new GeoJSON({});
    const features = result.format.readFeatures({
      type: 'FeatureCollection',
      features: [fieldFeature1, fieldFeature2],
    });
    result.parser = new jsts.io.OL3Parser();
    // @ts-ignore: ts doesn't have an up-to-date type for parser instance
    result.parser.inject(
      Point,
      LineString,
      LinearRing,
      Polygon,
      MultiPoint,
      MultiLineString,
      MultiPolygon,
    );
    const field1Geom = result.parser.read(features[0].getGeometry());
    const field2Geom = result.parser.read(features[1].getGeometry());
    let isIntersecting = this.jstsHelper.safeIntersects(field1Geom, field2Geom);

    if (isIntersecting) {
      try {
        const union = field1Geom.union(field2Geom);
        isIntersecting = union.getNumGeometries() === 1;
      } catch (error) {
        console.error('Error while performing union operation:', error);
        isIntersecting = false;
      }
    }

    if (!isIntersecting) {
      return { ...result, isIntersecting };
    }

    return {
      ...result,
      isIntersecting,
      success: true,
      field1Geom,
      field2Geom,
    };
  }

  merge(result: CombineGeometryPrepareResult): Feature {
    const union = result.field1Geom.union(result.field2Geom);
    const parsedUnion = result.parser.write(union) as Geometry;
    const newFieldGeometry = result.format.writeGeometryObject(parsedUnion);

    return {
      id: null,
      cropId: null,
      properties: {},
      type: 'Feature',
      geometry: newFieldGeometry as GeoJSON.Polygon,
    };
  }
}
